import {useEffect, useRef} from "react";
import Konva from "konva";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";
import { logDebug, ViewportIdentifier, isNullOrUndefined } from "../js/Common";
import {createBasePipeline} from "../CenterlineMeasurement/BranchedPoints/CenterlineMeasurementPipeline";
import { CenterlineBranchSelection, CenterlineType, CenterlineDistanceBranchSelection } from "../CenterlineMeasurement/CenterlinePlaneShared";
import { makeRootFilter } from "../StentPlanning/StentPlanning.js";
import { applyWallCenterlineUpdate } from "../js/CenterlineUtils";
const isDebugging = false;

function log(msg) {
    logDebug("Visualization.StentPlanningCenterlines", isDebugging, msg);
}

/**
 * @typedef {Object} Point2D
 * @property {number} x - The x-coordinate.
 * @property {number} y - The y-coordinate.
 */

// TODO: Use Vector3 from js/Vector3.js instead?
/**
 * @typedef {Object} Point3D
 * @property {number} x - The x-coordinate.
 * @property {number} y - The y-coordinate.
 * @property {number} z - The z-coordinate.
 */

/**
 * @typedef {Object} PickedPoint
 * @property {Point3D} WorldCoords - The world coordinates of the picked point.
 * @property {number} Value - The value associated with the picked point.
 */

/**
 * @typedef {Object} Branch
 * @property {string} centerlineBranchType
 * @property {string} centerlineBranchSelection
 */

/**
 * @typedef {Object} UnifiedDistanceBranchRange
 * @property {Object} branch
 * @property {number} firstIndex
 * @property {number} secondIndex
 */

/**
 * @typedef {Object} StentPlanningCenterlinesProps
 * @property {UnifiedDistanceBranchRange[]} [lengths] - The positions of the planes in the stent planning.
 * @property {string} viewportIdentifier - The viewport identifier.
 */

/**
 * Computes the on-screen pixel location corresponding to the given world coordinates.
 *
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {object} worldCoords - The world coordinates { x: number, y: number, z: number }.
 * @param {number} x - The x-coordinate.
 * @param {number} y - The y-coordinate.
 * @param {number} z - The z-coordinate.
 * @returns {Point2D} The on-screen pixel location { x: number, y: number }.
 */
function computeScreenCoordinates(renderer, x, y, z) {
    const coordinate = vtkCoordinate.newInstance();
    coordinate.setCoordinateSystemToWorld();
    coordinate.setValue([x, y, z]);
    const displayCoords = coordinate.getComputedDisplayValue(renderer);

    // The y-coordinate is inverted in VTK relative to HTML/CSS, so we need to correct it.
    const renderWindow = renderer.getRenderWindow();
    const canvas = renderWindow.getViews()[0].getCanvas();
    const scale = 1 / window.devicePixelRatio;
    const invertedY = canvas.clientHeight - (displayCoords[1] * scale);
    return { x: displayCoords[0] * scale, y: invertedY };
}

/**
 * Computes the on-screen pixel location corresponding to the given world coordinates.
 *
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {TypedArray} coords - The complete set of world coordinates [x1, y1, z1, x2, y2, z2, ...].
 * @returns {Point2D[]} The on-screen pixel location { x: number, y: number }.
 */
function computeAllScreenCoordinates(renderer, coords) {
    const numPoints = coords.length / 3;
    const screenCoords = new Array(numPoints);

    for (let i = 0; i < numPoints; i++) {
        screenCoords[i] = computeScreenCoordinates(renderer, coords[i * 3], coords[i * 3 + 1], coords[i * 3 + 2]);
    }

    return screenCoords;
}

const centerlineOffsets = [ 0, 5, -4, 4 ];

/**
 * Computes the screen positions of the centerline.
 *
 * @param {vtkRenderer} renderer
 * @param {vtkUnifiedBranchFilter} rootFilter
 * @param {UnifiedDistanceBranchRange} length
 * @param {number} offset
 * @returns {number[]}
 */
function getCenterlineScreenPositions(renderer, rootFilter, length, offset) {
    const unifiedPolyData = rootFilter.getOutputData(0);
    const startIndex = Math.min(length.firstIndex, length.secondIndex);
    const endIndex = Math.max(length.firstIndex, length.secondIndex);
    const centerlinePositions = unifiedPolyData.getPoints().getTuples(startIndex, endIndex + 1);
    const screenCenterlinePositions = computeAllScreenCoordinates(renderer, centerlinePositions);
    return screenCenterlinePositions.flatMap(point => [point.x + offset, point.y]);
}

/**
 * Draws a highlight for the centerline.
 *
 * @param {number[]} screenCenterlinePositions
 * @param {Konva.Layer} layer
 */
function drawCenterlineHighlight(screenCenterlinePositions, layer, highlightColor, opacity, isTop) {
    const scale = 1 / window.devicePixelRatio;
    const strokeWidth = Math.max(3, 3 * scale);
    const color = highlightColor;

    const centerlineHighlight = new Konva.Line({
        points: screenCenterlinePositions.some(isNaN) ? [0, 0, 0, 0] : screenCenterlinePositions,
        stroke: color,
        strokeWidth: strokeWidth,
        opacity: opacity
    });

    layer.add(centerlineHighlight);
    if (isTop) {
        centerlineHighlight.moveToTop();
    } else {
        centerlineHighlight.moveToBottom();
    }
}

function updateCenterlineLayer(context, stentPlanningCenterlinesProps, fieldDataArrayNames, meshes, parentContext) {
    if (stentPlanningCenterlinesProps.lengths && meshes && context.current.stage) {
        context.current.stentPlanningCenterlinesProps = stentPlanningCenterlinesProps;
        const { lengths, viewportIdentifier, fieldDataArrayNames } = stentPlanningCenterlinesProps;
        log("Updating centerline highlight");

        if (context.current.centerlineLayer) {
            context.current.centerlineLayer.destroy();
            context.current.centerlineLayer = null;
        }

        const centerlineLayer = new Konva.Layer();

        function drawCenterline(length, offset, highlightColor, opacity, isTop) {
            // If the plane indices are the same, there's no centerline to draw.
            if (length.firstIndex !== length.secondIndex) {

                var centerlineBranchSelection;
                if (length.branch.name === 'Left') {
                    centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_LEFT;
                } else if (length.branch.name === 'Right') {
                    centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_RIGHT;
                } else if (length.branch.name === 'Cusp') {
                    centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_CUSP;
                }

                const rootFilter =
                    makeRootFilter(
                        CenterlineType.WALL,
                        centerlineBranchSelection,
                        meshes,
                        fieldDataArrayNames
                    );

                const highlightedCenterlinePositionsFlat =
                    getCenterlineScreenPositions(
                        parentContext.renderer,
                        rootFilter,
                        length,
                        offset
                    );

                drawCenterlineHighlight(
                    highlightedCenterlinePositionsFlat,
                    centerlineLayer,
                    highlightColor,
                    opacity,
                    isTop
                );
            }
        }
        function drawFullCenterline() {
            const centerlineFieldData = meshes.wallCenterline.dataSource.getFieldData();
            const [distanceCuspMin, distanceCuspMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk_Cusp").getRange();
            const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
            const distanceLeftNumberOfTuples = centerlineFieldData.getArrayByName("Distance_Branch_Left").getNumberOfTuples();
            const distanceRightNumberOfTuples = centerlineFieldData.getArrayByName("Distance_Branch_Right").getNumberOfTuples();

            const cuspLength =
                { branch: { name: CenterlineDistanceBranchSelection.CUSP }, firstIndex: distanceCuspMin, secondIndex: distanceCuspMax };

            const leftLength =
                { branch: { name: CenterlineDistanceBranchSelection.LEFT }, firstIndex: distanceTrunkMax, secondIndex: distanceTrunkMax + distanceLeftNumberOfTuples };

            const rightLength =
                { branch: { name: CenterlineDistanceBranchSelection.RIGHT }, firstIndex: distanceTrunkMax, secondIndex: distanceTrunkMax + distanceRightNumberOfTuples };

            drawCenterline(cuspLength, 0, 'purple', 0.75, false);
            drawCenterline(leftLength, 0, 'purple', 0.75, false);
            drawCenterline(rightLength, 0, 'purple', 0.75, false);
        }


        for (var i in lengths) {
            log(i);
            const offset = lengths.length > 1 ? centerlineOffsets[i] : 0;
            const length = lengths[i];
            log(length);

            const lineColor = viewportIdentifier === ViewportIdentifier.MEASUREMENT ? 'purple' : 'LawnGreen';

            drawCenterline(length, offset, lineColor, 1.0, true);
        }

        if (viewportIdentifier === 'StentPlanning') drawFullCenterline();

        context.current.stage.add(centerlineLayer);
        context.current.centerlineLayer = centerlineLayer;
        centerlineLayer.moveToBottom();
        centerlineLayer.draw();
    } else {
        log("Removing centerline distance indicators");
        if (context.current.centerlineLayer) {
            context.current.centerlineLayer.destroy();
            context.current.centerlineLayer = null;
        }
    }
}

/**
 * Hook for stent planning visualization.
 *
 * @param {HTMLElement | null} container - The container for the hook.
 * @param {object} parentContext - The parent context for the hook.
 * @param {Stage} stage - The Konva stage.
 * @param {StentPlanningCenterlinesProps} stentPlanningCenterlinesProps - Properties for stent planning.
 */
export function useStentPlanningCenterlines(container, parentContext, stage, meshes, stentPlanningCenterlinesProps) {
    const context = useRef(null);

    // Side effect that only runs when the component is initially mounted, and cleans up when it is unmounted.
    useEffect(() => {
        log("Mounting");

        return () => {
            log("Unmounting");

            // Just using a more convenient name; no magic here.
            const ctx = context.current;

            if (ctx) {
                log("Cleaning up");

                // TODO: Should probably delete the actors, mapper, and sources as well.
                if (parentContext?.renderer && ctx.firstConeActor) {
                    parentContext.renderer.removeActor(ctx.firstConeActor);
                }

                if (parentContext?.renderer && ctx.lastConeActor) {
                    parentContext.renderer.removeActor(ctx.lastConeActor);
                }

                if (parentContext?.renderer && ctx.centerlineActor) {
                    parentContext.renderer.removeActor(ctx.centerlineActor);
                }

                if (ctx.resizeObserver) {
                    ctx.resizeObserver.disconnect();
                }
                if (ctx.eventHandler) {
                    window.removeEventListener("screenshotUpdate", ctx.eventHandler);
                }
                ctx.valueLabel = null;
                ctx.firstConeActor = null;
                ctx.firstConeSource = null;
                ctx.lastConeSource = null;
                ctx.lastConeActor = null;
                ctx.centerlineActor = null;
                ctx.centerlineLayer = null;
                ctx.stentPlanningCenterlinesProps = null;
            }
        };
    }, []);

    const { lengths, viewportIdentifier, fieldDataArrayNames  } = stentPlanningCenterlinesProps;
    // Side effect to initialize the stent planning visualization.
    useEffect(() => {
        if (container && !context.current && parentContext) {
            log("Initializing");
            var stentContainer = null;

            if (viewportIdentifier === ViewportIdentifier.MEASUREMENT) {
                stentContainer = document.createElement('div');
                stentContainer.id = 'stentContainer' + viewportIdentifier;
                stentContainer.style.position = 'absolute';
                stentContainer.style.top = '0';
                stentContainer.style.left = '0';
                stentContainer.style.width = '100%';
                stentContainer.style.height = '100%';
                container.appendChild(stentContainer);
            }

            context.current = {
                stentContainer: stentContainer,
                stage: null,
                valueLabel: null,
                line: null,
                renderer: null,
                firstConeActor: null,
                firstConeSource: null,
                lastConeActor: null,
                lastConeSource: null,
                centerlineActor: null,
                centerlineLayer: null,
                resizeObserver: null
            };
        }

        // TODO: Technically we should handle cases where the container is removed or swapped, but in practice this may never happen.

    }, [container, parentContext]);

    //Set up resize observer
    useEffect(() => {
        if (container && context.current && stentPlanningCenterlinesProps && fieldDataArrayNames && meshes && parentContext) {
            context.current.stentPlanningCenterlinesProps = stentPlanningCenterlinesProps;
            const handleResize = () => {
                log("Resizing");
                if (context.current?.stage && context.current?.centerlineLayer) {
                    const props = context.current.stentPlanningCenterlinesProps;
                    context.current.stage.width(container.clientWidth);
                    context.current.stage.height(container.clientHeight);
                    updateCenterlineLayer(context, props, fieldDataArrayNames, meshes, parentContext);
                    context.current.centerlineLayer.draw();
                }
            };
            if (isNullOrUndefined(context.current.resizeObserver)) {
                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(container);
                context.current.resizeObserver = resizeObserver;
            }
        }
    }, [container, stentPlanningCenterlinesProps, meshes, parentContext, fieldDataArrayNames]);

    useEffect(() => {
        if (container && parentContext?.renderer && context.current && meshes && fieldDataArrayNames && stentPlanningCenterlinesProps) {
            context.current.stentPlanningCenterlinesProps = stentPlanningCenterlinesProps;
            if (!context.current.stage) {
                if (stentPlanningCenterlinesProps.viewportIdentifier === ViewportIdentifier.MEASUREMENT) {
                    log("Creating stage for measurement viewport");
                    context.current.stage = new Konva.Stage({
                        container: context.current.stentContainer.id,
                        width: container.clientWidth,
                        height: container.clientHeight
                    });
                } else if (stage) {
                    context.current.stage = stage;
                }
            }
            updateCenterlineLayer(context, stentPlanningCenterlinesProps, fieldDataArrayNames, meshes, parentContext);
            let handleScreenshotUpdate = () => {updateCenterlineLayer(context, stentPlanningCenterlinesProps, fieldDataArrayNames, meshes, parentContext);};
            window.addEventListener("screenshotUpdate", handleScreenshotUpdate);
            context.current = { ...context.current, eventHandler: handleScreenshotUpdate };
        }
    }, [container, parentContext, meshes, stage, stentPlanningCenterlinesProps]);

    // Re-draw the centerline overlay when the wallCenterline points are updated.
    // Each viewport has its own wallCenterline JS object; apply the received coordinates
    // to this viewport's instance before redrawing.
    useEffect(() => {
        const handleCenterlineUpdate = (ev) => {
            const ds = meshes?.wallCenterline?.dataSource;
            applyWallCenterlineUpdate(ds, ev.detail);
            if (context.current?.stage && meshes && fieldDataArrayNames && parentContext?.renderer) {
                const props = context.current.stentPlanningCenterlinesProps || stentPlanningCenterlinesProps;
                updateCenterlineLayer(context, props, fieldDataArrayNames, meshes, parentContext);
            }
        };
        window.addEventListener('wallCenterlineUpdated', handleCenterlineUpdate);
        return () => window.removeEventListener('wallCenterlineUpdated', handleCenterlineUpdate);
    }, [meshes, fieldDataArrayNames, parentContext, stentPlanningCenterlinesProps]);
}
